/**
 * Day pager for the parent view: ◀ <date> ▶ plus a date input for a direct jump
 * and a "Today" shortcut when the cursor has moved away.
 *
 * The cursor lives in the URL (?date=YYYY-MM-DD) so a look-back survives a
 * refresh and can be linked to from the history table. Bounds come from the
 * shared day-cursor module, so this and the mobile pager obey one rule: never
 * into the future, never before treatment start.
 */
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  canStepDay,
  clampDay,
  dayBounds,
  stepDay,
  toCalendarDate,
  type CalendarDate,
  type DayBounds,
} from '@toxofollow/shared';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';

interface PatientResp {
  patient: { treatmentStartDate: string | null } | null;
}

/** Format a calendar date for display, in UTC to match the shared day math. */
export function formatDay(day: CalendarDate, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'fr-FR', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${day}T00:00:00Z`));
  } catch {
    return day;
  }
}

/** The selected day, backed by ?date=, clamped to the reachable range. */
export function useDayParam(): [CalendarDate, (d: CalendarDate) => void, DayBounds, CalendarDate] {
  const [params, setParams] = useSearchParams();
  const patientQ = useQuery({ queryKey: ['patient'], queryFn: () => api.get<PatientResp>('/patient') });
  const today = toCalendarDate(new Date());
  const treatmentStart = patientQ.data?.patient?.treatmentStartDate ?? null;
  const bounds = useMemo(() => dayBounds(treatmentStart, today), [treatmentStart, today]);

  const raw = params.get('date');
  const day = clampDay(raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : today, bounds);

  const setDay = (d: CalendarDate) => {
    const next = clampDay(d, bounds);
    const p = new URLSearchParams(params);
    if (next === today) p.delete('date');
    else p.set('date', next);
    setParams(p, { replace: true });
  };

  return [day, setDay, bounds, today];
}

export function DayPager({
  day,
  bounds,
  today,
  onChange,
  extra,
}: {
  day: CalendarDate;
  bounds: DayBounds;
  today: CalendarDate;
  onChange: (d: CalendarDate) => void;
  extra?: ReactNode;
}) {
  const { t, locale } = useI18n();
  const label = formatDay(day, locale);
  const canPrev = canStepDay(day, -1, bounds);
  const canNext = canStepDay(day, 1, bounds);

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label={t('day.prev')}
          title={t('day.prev')}
          disabled={!canPrev}
          onClick={() => onChange(stepDay(day, -1, bounds))}
          className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 disabled:opacity-40"
        >
          ◀
        </button>

        <div className="flex-1 text-center">
          <div className="font-semibold text-slate-800">{label}</div>
          <input
            type="date"
            aria-label={t('history.colDate')}
            value={day}
            min={bounds.min}
            max={bounds.max}
            onChange={(e) => e.target.value && onChange(e.target.value)}
            className="mt-1 text-xs text-slate-500 border border-slate-200 rounded px-2 py-1"
          />
        </div>

        <button
          type="button"
          aria-label={t('day.next')}
          title={t('day.next')}
          disabled={!canNext}
          onClick={() => onChange(stepDay(day, 1, bounds))}
          className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 disabled:opacity-40"
        >
          ▶
        </button>
      </div>

      {day !== today && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
          <span className="text-xs text-amber-800">{t('day.backdated', { date: label })}</span>
          <button type="button" onClick={() => onChange(today)} className="text-xs font-medium text-amber-900 underline">
            {t('day.today')}
          </button>
        </div>
      )}

      {extra}
    </div>
  );
}
